/**
 * Template Selector Component
 *
 * Displays a grid of available form templates for the user to choose from.
 * Used when creating a new form to quickly start with pre-built templates.
 * Clicking a template card immediately proceeds with that template.
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FormTemplateType,
  FormTemplate,
  formTemplates,
  getTemplateColor,
  getTemplateBorderColor,
} from '@/lib/forms/templates';

interface TemplateSelectorProps {
  onSelectAndProceed: (templateType: FormTemplateType) => void | Promise<void>;
  onBack?: () => void;
  isLoading?: boolean;
  className?: string;
}

export function TemplateSelector({
  onSelectAndProceed,
  onBack,
  isLoading = false,
  className,
}: TemplateSelectorProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplateType | null>(null);

  const handleTemplateClick = async (templateType: FormTemplateType) => {
    if (isLoading) return;
    setSelectedTemplate(templateType);
    await onSelectAndProceed(templateType);
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header Card */}
      <Card className="p-4">
        <div className="flex items-center">
          {onBack && (
            <Button variant="outline" size="sm" onClick={onBack} disabled={isLoading}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          <div className="flex-1 text-center">
            <h2 className="text-lg font-semibold">Choose a Template</h2>
            <p className="text-sm text-muted-foreground">
              Click a template to create your form
            </p>
          </div>
          {/* Spacer to balance the back button */}
          {onBack && <div className="w-[73px]" />}
        </div>
      </Card>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {formTemplates.map((template, index) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate === template.id}
            isLoading={isLoading && selectedTemplate === template.id}
            onClick={() => handleTemplateClick(template.id)}
            index={index}
            disabled={isLoading}
          />
        ))}
      </div>
    </div>
  );
}

interface TemplateCardProps {
  template: FormTemplate;
  isSelected: boolean;
  isLoading?: boolean;
  onClick: () => void;
  index: number;
  disabled?: boolean;
}

function TemplateCard({ template, isSelected, isLoading, onClick, index, disabled }: TemplateCardProps) {
  const Icon = template.icon;
  const colorClass = getTemplateColor(template.id);
  const borderColorClass = getTemplateBorderColor(template.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <Card
        className={cn(
          'cursor-pointer transition-all hover:shadow-md relative overflow-hidden h-full flex flex-col',
          isSelected && `ring-2 ring-primary ${borderColorClass}`,
          !isSelected && !disabled && 'hover:border-primary/50',
          disabled && !isSelected && 'opacity-50 cursor-not-allowed'
        )}
        onClick={disabled ? undefined : onClick}
      >
        {/* Loading Indicator */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        <CardHeader className="pb-3">
          {/* Icon */}
          <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center mb-3', colorClass)}>
            <Icon className="h-6 w-6" />
          </div>

          <CardTitle className="text-lg">{template.name}</CardTitle>
          <CardDescription className="text-sm">
            {template.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-0 flex-1 flex flex-col justify-end">
          {/* Features */}
          <div className="flex flex-wrap gap-1.5">
            {template.features.slice(0, 3).map((feature, idx) => (
              <Badge
                key={idx}
                variant="default"
                className="text-xs font-normal"
              >
                {feature}
              </Badge>
            ))}
            {template.features.length > 3 && (
              <Badge variant="default" className="text-xs font-normal opacity-70">
                +{template.features.length - 3} more
              </Badge>
            )}
          </div>

          {/* Question Count Preview */}
          {template.defaultQuestions.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              {template.defaultQuestions.length} pre-built question{template.defaultQuestions.length !== 1 ? 's' : ''}
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

/**
 * Compact Template Badge Component
 * Used in the form builder header to show current template type (read-only)
 */
interface TemplateBadgeProps {
  templateType: FormTemplateType;
  className?: string;
}

export function TemplateBadge({ templateType, className }: TemplateBadgeProps) {
  const template = formTemplates.find(t => t.id === templateType);
  if (!template) return null;

  const Icon = template.icon;
  const colorClass = getTemplateColor(templateType);

  return (
    <Badge
      variant="outline"
      className={cn(
        'gap-1.5',
        colorClass,
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {template.name}
    </Badge>
  );
}
